import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import Local from 'modules/Local';
import {
  Creative,
  CreativeId,
  CreativeMetaOperation,
  CreativeMetaOperationsModel,
  CreativesMetaFormModel,
  CreativeStatus,
  PreparedCreativesListItem,
  UpdateCreativeResponseError,
} from 'components/common/types/Creative.types';
import { getCreativeError, getCreativesMotionType } from './reducerUtils';

export interface Creatives {
  creativesIdsList: string[];
  selectedCreatives: Creative[];
  metadata: CreativesMetaFormModel;
  operations: CreativeMetaOperationsModel;
  pendingCreativesCount?: number;
}

const initialState: Creatives = {
  creativesIdsList: [],
  selectedCreatives: [],
  metadata: {
    dealId: [],
    productFormat: [],
    advertiser: null,
    brand: null,
    productCategory: null,
    agency: null,
    specialist: null,
    movement: null,
  },
  operations: {
    dealId: CreativeMetaOperation.ADD,
    productFormat: CreativeMetaOperation.ADD,
  },
  pendingCreativesCount: undefined,
};

export const creativesSlice = createSlice({
  name: 'contentManagementCreatives',
  initialState,
  reducers: {
    setCreativesIdsList: (state, action: PayloadAction<PreparedCreativesListItem[]>) => {
      state.creativesIdsList = action.payload.map((creative) => creative.id);
    },
    selectCreatives: (state, action: PayloadAction<Partial<Creative>[]>) => {
      state.selectedCreatives = action.payload.map(
        (creative) =>
          ({
            ...creative,
            error: {},
          }) as Creative,
      );
      state.metadata.movement = getCreativesMotionType(action.payload);
    },
    resetCreativesState: () => {
      return { ...initialState };
    },
    removeSelectedCreative: (state, action: PayloadAction<CreativeId>) => {
      state.selectedCreatives = [...state.selectedCreatives.filter((s) => s.id !== action.payload)];
    },
    setCreativeMeta: (state, action: PayloadAction<Partial<CreativesMetaFormModel>>) => {
      state.metadata = {
        ...state.metadata,
        ...(action.payload ?? initialState.metadata),
      };
      state.selectedCreatives = state.selectedCreatives.map(
        (creative) =>
          ({
            ...creative,
            movement: action.payload.movement ?? creative.movement,
            market: creative.market.map((market) => ({
              ...market,
              ...(market.environment === Local.getEnvironmentId()
                ? {
                    commercial: {
                      ...market.commercial,
                      advertiser: action.payload.advertiser ?? market.commercial?.advertiser,
                      brand: action.payload.brand ?? market.commercial?.brand,
                    },
                  }
                : {}),
            })),
          }) as Creative,
      );
    },
    setCreativeMetaOperations: (state, action: PayloadAction<CreativeMetaOperationsModel>) => {
      state.operations = {
        ...state.operations,
        ...action.payload,
      };
    },
    updateCreativesStatus: (state, action: PayloadAction<CreativeStatus>) => {
      state.selectedCreatives = [
        ...state.selectedCreatives.map((creative) => ({
          ...creative,
          statusCode: action.payload,
        })),
      ];
    },
    clearCreativesMetaAndResetMotionType: (state) => {
      return {
        ...state,
        metadata: {
          ...initialState.metadata,
          movement: getCreativesMotionType(state.selectedCreatives),
        },
        operations: {
          ...initialState.operations,
        },
      };
    },
    updateCreativesErrorStatus: (state, action: PayloadAction<UpdateCreativeResponseError[]>) => {
      return {
        ...state,
        selectedCreatives: [
          ...state.selectedCreatives.map((creative) => ({
            ...creative,
            error: getCreativeError(creative, action.payload),
          })),
        ],
      };
    },
    updatePendingCreativesCount: (state, action: PayloadAction<number>) => {
      state.pendingCreativesCount = action.payload;
    },
  },
});

export const {
  setCreativesIdsList,
  selectCreatives,
  resetCreativesState,
  removeSelectedCreative,
  setCreativeMeta,
  setCreativeMetaOperations,
  updateCreativesStatus,
  clearCreativesMetaAndResetMotionType,
  updateCreativesErrorStatus,
  updatePendingCreativesCount,
} = creativesSlice.actions;

export default creativesSlice.reducer;
